/**
 * File: ADRContentPage.tsx
 * Description: Page for viewing and editing ADR content using Monaco Markdown Editor
 * Author: GitHub Copilot
 * Created: 2025-09-12
 * Last Modified: 2025-09-12
 */

import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import { MonacoMarkdownEditor } from '../../components/monaco/MonacoMarkdownEditor';
import { adrService } from '../../services/adrService';

const MISSING_PARAMS_ERROR = 'Missing project ID or ADR ID';

const ADRContentPage: React.FC = () => {
  const { projectId, adrId } = useParams<{
    projectId: string;
    adrId: string;
  }>();
  const navigate = useNavigate();

  // State management
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /**
   * Save ADR content to the backend
   */
  const saveContent = async () => {
    if (!projectId || !adrId) {
      setError(MISSING_PARAMS_ERROR);
      return;
    }

    try {
      setSaving(true);
      await adrService.saveADRGeneratedContent(
        Number(projectId),
        adrId,
        content,
      );

      setHasUnsavedChanges(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle content changes in the editor
   */
  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setHasUnsavedChanges(true);
  };

  /**
   * Handle edit mode changes
   */
  const handleEditModeChange = (editing: boolean) => {
    setIsEditing(editing);
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    if (hasUnsavedChanges) {
      if (globalThis.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(`/project/${projectId}/adrs`);
      }
    } else {
      navigate(`/project/${projectId}/adrs`);
    }
  };

  // Fetch content on component mount
  useEffect(() => {
    const fetchContent = async () => {
      
      if (!projectId || !adrId) {
        setError(MISSING_PARAMS_ERROR);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        const data = await adrService.getADRGeneratedContent(
          Number(projectId),
          adrId,
        );
        
        setContent(data || '# ADR Content\n\nStart writing your ADR content here...');
        setError(null);
      } catch (err) {
        // Log error silently for debugging
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Unknown error occurred');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [projectId, adrId]);

  // Listen for ADR content generation events from chat
  useEffect(() => {
    const handleContentGenerated = (event: Event) => {
      const customEvent = event as CustomEvent<{ 
        adrId?: string; 
        projectId?: number | string 
      }>;
      const { adrId: eventAdrId, projectId: eventProjectId } = customEvent.detail ?? {};

      // Only refresh if it's for the current ADR and project
      if (eventAdrId !== adrId) {
        return;
      }

      if (projectId && eventProjectId && projectId !== String(eventProjectId)) {
        return;
      }

      // Refresh the content
      const refreshContent = async () => {
        if (!adrId || !projectId) {
          return;
        }

        try {
          const data = await adrService.getADRGeneratedContent(
            Number(projectId),
            adrId,
          );
          setContent(data || '# ADR Content\n\nStart writing your ADR content here...');
          setHasUnsavedChanges(false);
          setError(null);
        } catch (err) {
          if (err instanceof Error) {
            setError(err.message);
          }
        }
      };

      refreshContent();
    };

    globalThis.addEventListener('adrContentGenerated', handleContentGenerated as EventListener);

    return () => {
      globalThis.removeEventListener('adrContentGenerated', handleContentGenerated as EventListener);
    };
  }, [projectId, adrId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-base-100">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-base-300 bg-base-200">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBack}
            className="btn btn-ghost btn-sm main-button"
            aria-label="Go back to ADR list"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to ADRs
          </button>
          <div>
            <h1 className="text-lg font-semibold">ADR Content</h1>
            <p className="text-sm text-base-content/70">
              Project {projectId} • ADR {adrId}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {hasUnsavedChanges && (
            <span className="text-warning text-sm">
              Unsaved changes
            </span>
          )}
          <button
            onClick={saveContent}
            disabled={!hasUnsavedChanges || saving}
            className="btn btn-primary btn-sm main-button"
          >
            {saving ? (
              <span className="loading loading-spinner loading-sm"></span>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-error mx-4 mt-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Monaco Markdown Editor */}
      <div className="flex-1 min-h-0 p-4">
        <div className="h-full border border-base-300 rounded-lg overflow-hidden">
          <MonacoMarkdownEditor
            value={content}
            onChange={handleContentChange}
            height="100%"
            theme="light"
            placeholder="Start writing your ADR content here..."
            isEditable={true}
            isEditing={isEditing}
            onEditModeChange={handleEditModeChange}
            onSave={saveContent}
            isSaving={saving}
            initialFullscreen={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ADRContentPage;
